import React, { useState } from "react";
import { usePricingConfigStore } from "@/stores/usePricingConfigStore";
import { trpc } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, Download } from "lucide-react";
import { exportToExcel } from "@/lib/excel.utils";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  pricingConfigWastageSchema,
  PricingConfigWastageForm,
} from "@/lib/schemas/pricing-config";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const WastageConfigTab = () => {
  const queryClient = useQueryClient();
  const {
    isWastageModalOpen,
    selectedWastageId,
    openWastageModal,
    closeWastageModal,
  } = usePricingConfigStore();
  const { data: wastagesData, isLoading } = useQuery(
    trpc.pricingConfig.getWastages.queryOptions(),
  );
  const wastages = wastagesData as unknown as any[];

  const deleteMutation = useMutation({
    ...trpc.pricingConfig.deleteWastage.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã xóa cấu hình hao phí");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getWastages.queryKey(),
      });
    },
    onError: (err: any) => {
      toast.error("Lỗi khi xóa cấu hình hao phí: " + err.message);
    },
  });

  const handleDelete = (id: string) => {
    if (confirm("Bạn có chắc chắn muốn xóa?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2">
        <Button
          variant="outline"
          onClick={() =>
            exportToExcel(wastages || [], [
              { key: "processType", label: "Loại công đoạn", width: 18 },
              { key: "minQty", label: "Sản lượng từ (tờ)", width: 16, format: "number" },
              { key: "maxQty", label: "Sản lượng đến (tờ)", width: 16, format: "number" },
              { key: "setupWasteSheets", label: "Hao phí setup (tờ)", width: 16, format: "number" },
              { key: "runWastePercent", label: "Hao phí chạy máy (%)", width: 18, format: "number" },
            ], "ty_le_hao_phi")
          }
        >
          <Download className="w-4 h-4 mr-2" /> Xuất Excel
        </Button>
        <Button onClick={() => openWastageModal()}>
          <Plus className="w-4 h-4 mr-2" /> Thêm hao phí
        </Button>
      </div>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Loại công đoạn</TableHead>
              <TableHead>Sản lượng từ (tờ)</TableHead>
              <TableHead>Sản lượng đến (tờ)</TableHead>
              <TableHead>Hao phí setup (tờ)</TableHead>
              <TableHead>Hao phí chạy máy (%)</TableHead>
              <TableHead className="text-right">Thao tác</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : wastages?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-10 text-muted-foreground"
                >
                  Chưa có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              wastages?.map((wastage) => (
                <TableRow key={wastage.id} className="cursor-pointer" onClick={() => openWastageModal(wastage.id)}>
                  <TableCell className="font-medium">
                    {wastage.processType}
                  </TableCell>
                  <TableCell>
                    {Number(wastage.minQty).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {Number(wastage.maxQty).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {Number(wastage.setupWasteSheets).toLocaleString()}
                  </TableCell>
                  <TableCell>{Number(wastage.runWastePercent)}%</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(wastage.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <WastageConfigModal
        isOpen={isWastageModalOpen}
        onClose={closeWastageModal}
        wastageId={selectedWastageId}
      />
    </div>
  );
};

const WastageConfigModal = ({
  isOpen,
  onClose,
  wastageId,
}: {
  isOpen: boolean;
  onClose: () => void;
  wastageId: string | null;
}) => {
  const queryClient = useQueryClient();
  const { data: wastageData, isLoading: isLoadingWastage } = useQuery({
    ...trpc.pricingConfig.getWastageById.queryOptions(wastageId!),
    enabled: !!wastageId,
  });
  const wastage = wastageData as unknown as any;

  const form = useForm<PricingConfigWastageForm>({
    resolver: zodResolver(pricingConfigWastageSchema),
    defaultValues: {
      processType: "",
      minQty: 0,
      maxQty: 0,
      setupWasteSheets: 0,
      runWastePercent: 0,
    },
  });

  React.useEffect(() => {
    if (wastage) {
      form.reset({
        ...wastage,
        minQty: Number(wastage.minQty) || 0,
        maxQty: Number(wastage.maxQty) || 0,
        setupWasteSheets: Number(wastage.setupWasteSheets) || 0,
        runWastePercent: Number(wastage.runWastePercent) || 0,
      });
    } else {
      form.reset({
        processType: "",
        minQty: 0,
        maxQty: 0,
        setupWasteSheets: 0,
        runWastePercent: 0,
      });
    }
  }, [wastage, isOpen, form]);

  const createMutation = useMutation({
    ...trpc.pricingConfig.createWastage.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã thêm cấu hình hao phí");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getWastages.queryKey(),
      });
      onClose();
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const updateMutation = useMutation({
    ...trpc.pricingConfig.updateWastage.mutationOptions(),

    onSuccess: () => {
      toast.success("Đã cập nhật cấu hình hao phí");
      queryClient.invalidateQueries({
        queryKey: trpc.pricingConfig.getWastages.queryKey(),
      });
      onClose();
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const onSubmit = (values: PricingConfigWastageForm) => {
    if (wastageId) {
      updateMutation.mutate({ id: wastageId, data: values });
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {wastageId ? "Cập nhật cấu hình hao phí" : "Thêm cấu hình hao phí"}
          </DialogTitle>
        </DialogHeader>

        {wastageId && isLoadingWastage ? (
          <div className="flex justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="processType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Loại công đoạn (VD: OFFSET, DIE_CUT...)
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="minQty"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sản lượng từ</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxQty"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sản lượng đến</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="setupWasteSheets"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hao phí chuẩn bị máy (Tờ)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="runWastePercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hao phí chạy máy (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Hủy
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Đang lưu..."
                    : "Lưu cấu hình"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WastageConfigTab;
